import { familyService, type FamilyGoal, type MemberLimitCheck } from "@/services/familyService";
import { resolveFamilyNetWorth, type FamilyDashboardStats } from "@/utils/familyDashboardStats";
import type { FamilyData, FamilyMember } from "./useOptimizedFamily";

/**
 * Fetch Family Data For User.
 */
export async function fetchFamilyDataForUser(userId: string): Promise<FamilyData> {
  if (process.env.NODE_ENV === "development") {
    console.warn("🔄 Fetching family data for user:", userId);
  }

  try {
    const familyData = await familyService.getFamilyGroup();

    if (!familyData?.family) {
      if (process.env.NODE_ENV === "development") {
        console.warn("No family found for user");
      }
      return {
        family: null,
        familyMembers: [],
        familyGoals: [],
        familyLicense: null,
      };
    }

    if (process.env.NODE_ENV === "development") {
      console.warn("✅ Family data fetched successfully:", {
        family: familyData.family.name,
        members: familyData.familyMembers?.length || 0,
        goals: familyData.familyGoals?.length || 0,
      });
    }

    return familyData;
  } catch (error) {
    console.error("Error fetching family data:", error);
    const message = error instanceof Error ? error.message : "Failed to load family data";
    return {
      family: null,
      familyMembers: [],
      familyGoals: [],
      familyLicense: null,
      loadError: message,
    };
  }
}

/**
 * Build Local Family Dashboard Stats.
 */
export function buildLocalFamilyDashboardStats(
  familyData: FamilyData | undefined
): FamilyDashboardStats {
  const activeGoals = familyData?.familyGoals?.filter((g: FamilyGoal) => !g.is_archived) ?? [];
  const goalsTargetTotal = activeGoals.reduce(
    (sum: number, g: FamilyGoal) => sum + (g.target_amount ?? 0),
    0
  );

  return {
    total_members: familyData?.familyMembers?.length || 0,
    active_members:
      familyData?.familyMembers?.filter((m: FamilyMember) => m.is_active)?.length || 0,
    goals_target_total: goalsTargetTotal,
    goals_count: activeGoals.length,
    total_assets: 0,
    total_liabilities: 0,
    net_worth: 0,
  };
}

/**
 * Merge Remote Family Dashboard Stats.
 */
export function mergeRemoteFamilyDashboardStats(
  localStats: FamilyDashboardStats,
  remoteStats: Partial<FamilyDashboardStats> & {
    total_goals?: number;
    net_worth?: number;
  }
): FamilyDashboardStats {
  const totalAssets = remoteStats.total_assets ?? localStats.total_assets;
  const totalLiabilities = remoteStats.total_liabilities ?? localStats.total_liabilities;
  const netWorth = resolveFamilyNetWorth({
    net_worth: typeof remoteStats.net_worth === "number" ? remoteStats.net_worth : Number.NaN,
    total_assets: totalAssets,
    total_liabilities: totalLiabilities,
  });

  return {
    total_members: remoteStats.total_members ?? localStats.total_members,
    active_members: remoteStats.active_members ?? localStats.active_members,
    goals_target_total: remoteStats.total_goals ?? localStats.goals_target_total,
    goals_count: localStats.goals_count,
    total_assets: totalAssets,
    total_liabilities: totalLiabilities,
    net_worth: netWorth,
  };
}

/**
 * Build Dev Preview Dashboard Stats From Preview.
 */
export function buildDevPreviewDashboardStatsFromPreview(
  memberCount: number,
  goalCount: number
): FamilyDashboardStats {
  return {
    total_members: memberCount,
    active_members: memberCount,
    goals_target_total: 2500000,
    goals_count: goalCount,
    total_assets: 3200000,
    total_liabilities: 450000,
    net_worth: 2750000,
  };
}

export function buildDevPreviewMemberLimit(currentMembers: number): MemberLimitCheck {
  const maxAllowed = 5;
  return {
    current_members: currentMembers,
    max_allowed: maxAllowed,
    can_add_more: currentMembers < maxAllowed,
    needs_upgrade: currentMembers >= maxAllowed,
  };
}

/**
 * Parse Add Family Member Error.
 */
export function parseAddFamilyMemberError(error: unknown): string {
  if (typeof error === "object" && error !== null && "code" in error && error.code === "23505") {
    const errorObj = error as { code: string; message?: string };
    if (errorObj.message?.includes("family_members_email_unique")) {
      return "A family member with this email address already exists. Please use a different email.";
    }
    if (errorObj.message?.includes("family_members_pan_number_unique")) {
      return "A family member with this PAN number already exists in your family.";
    }
    return "This family member already exists. Please check the details and try again.";
  }

  if (typeof error === "object" && error !== null && "code" in error && error.code === "23503") {
    return "Invalid data provided. Please check all fields and try again.";
  }

  if (typeof error === "object" && error !== null && "code" in error && error.code === "23514") {
    return "Invalid data format. Please check all fields meet the requirements.";
  }

  if (error instanceof Error) {
    if (!error.message.includes("violates") && !error.message.includes("constraint")) {
      return error.message;
    }
  }

  return "Failed to add family member";
}

export interface PrimaryMemberPayloadInput {
  full_name: string;
  date_of_birth?: string;
  pan_number?: string;
}

export interface PrimaryMemberPayloadResult {
  payload: {
    full_name: string;
    date_of_birth?: string;
    pan_number?: string;
    encrypted_pan_number?: string;
    pan_encryption_iv?: string;
    masked_pan?: string;
  };
  panEncryptionSkipped: boolean;
}

/**
 * Build Primary Member Payload.
 */
export async function buildPrimaryMemberPayload(
  userId: string,
  primaryMember: PrimaryMemberPayloadInput,
  encryptPan: (
    userId: string,
    pan: string
  ) => Promise<{
    encrypted_pan_number: string;
    pan_encryption_iv: string;
    masked_pan: string;
  }>
): Promise<PrimaryMemberPayloadResult> {
  const panNumber = primaryMember.pan_number?.trim().toUpperCase();
  const payload: PrimaryMemberPayloadResult["payload"] = {
    full_name: primaryMember.full_name,
    date_of_birth: primaryMember.date_of_birth,
  };

  let panEncryptionSkipped = false;

  if (panNumber) {
    try {
      const encryptedPANData = await encryptPan(userId, panNumber);
      payload.pan_number = panNumber;
      payload.encrypted_pan_number = encryptedPANData.encrypted_pan_number;
      payload.pan_encryption_iv = encryptedPANData.pan_encryption_iv;
      payload.masked_pan = encryptedPANData.masked_pan;
    } catch (encryptionError) {
      console.warn("PAN encryption skipped during family create:", encryptionError);
      panEncryptionSkipped = true;
    }
  }

  return { payload, panEncryptionSkipped };
}

export type { FamilyLicense } from "@/services/familyService";
